import React, { useState, useEffect, createContext, useContext } from "react";
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  Navigate, 
  Link, 
  useNavigate 
} from "react-router-dom";
import { 
  Wrench, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar, 
  Clock, 
  Car, 
  CheckCircle2, 
  User as UserIcon, 
  ShieldCheck, 
  LogOut, 
  Plus, 
  Trash2, 
  Sliders, 
  AlertCircle, 
  ChevronRight, 
  DollarSign, 
  MapIcon, 
  Star, 
  Menu, 
  X, 
  MessageSquare,
  Sparkles
} from "lucide-react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import "@/App.css";

// ----------------- API Configuration -----------------
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios base defaults to enable cookie transmission for auth sessions
axios.defaults.withCredentials = true;

// ----------------- Auth Context & Protected Routes -----------------
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = not logged in, object = logged in
  const [loading, setLoading] = useState(true);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      setUser(response.data);
      toast.success("Welcome back!", { description: `Logged in as ${response.data.name}` });
      return { success: true };
    } catch (e) {
      const detail = e.response?.data?.detail || "Invalid credentials";
      toast.error("Login failed", { description: detail });
      return { success: false, error: detail };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, password });
      setUser(response.data);
      toast.success("Account created!", { description: `Welcome ${response.data.name}` });
      return { success: true };
    } catch (e) {
      const detail = e.response?.data?.detail || "Registration failed";
      toast.error("Registration failed", { description: detail });
      return { success: false, error: detail };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      setUser(false);
      toast.success("Logged out successfully");
    } catch (e) {
      toast.error("Logout failed");
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser: fetchCurrentUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  return useContext(AuthContext);
}

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="loading-screen">
        <div className="text-center">
          <Wrench className="h-10 w-10 animate-spin text-[#0A2342] mx-auto mb-4" />
          <p className="text-sm font-semibold text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (role && user.role !== role) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// ----------------- Layout & Header -----------------
function Header() {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="backdrop-blur-xl bg-white/80 border-b border-slate-200/50 sticky top-0 z-50 transition-all duration-200" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3 text-[#0A2342] hover:opacity-95 transition-opacity" data-testid="logo-brand">
              <div className="p-2.5 bg-[#0A2342] rounded-lg text-[#FF6B00]">
                <Wrench className="h-6 w-6 stroke-[2.5]" />
              </div>
              <div>
                <span className="font-extrabold text-xl tracking-tight uppercase block leading-none">D&G</span>
                <span className="text-xs font-bold text-[#FF6B00] tracking-widest uppercase block mt-1">Mobile Mechanic</span>
              </div>
            </Link>
          </div>
          
          <nav className="hidden md:flex space-x-8 text-sm font-semibold items-center">
            <a href="#services" className="text-slate-600 hover:text-[#0A2342] transition-colors" data-testid="nav-link-services">Services</a>
            <a href="#calculator" className="text-slate-600 hover:text-[#0A2342] transition-colors" data-testid="nav-link-calculator">Price Estimator</a>
            <a href="#how-it-works" className="text-slate-600 hover:text-[#0A2342] transition-colors" data-testid="nav-link-how">How it Works</a>
            <a href="#contact" className="text-slate-600 hover:text-[#0A2342] transition-colors" data-testid="nav-link-contact">Contact</a>
            
            {user ? (
              <div className="flex items-center space-x-4">
                {user.role === "admin" && (
                  <Link to="/admin" className="text-[#0A2342] hover:text-[#FF6B00] flex items-center gap-1.5" data-testid="nav-link-admin">
                    <ShieldCheck className="h-4 w-4" /> Admin Portal
                  </Link>
                )}
                <span className="text-slate-500 font-medium">Hello, {user.name}</span>
                <button 
                  onClick={logout} 
                  className="text-sm font-bold text-red-600 hover:text-red-700 flex items-center gap-1"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4" /> Sign Out
                </button>
              </div>
            ) : (
              <Link 
                to="/login" 
                className="bg-[#0A2342] text-white hover:bg-[#113059] px-5 py-2.5 rounded-lg transition-all shadow-sm"
                data-testid="login-link-btn"
              >
                Sign In
              </Link>
            )}
          </nav>

          <div className="flex md:hidden">
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-[#0A2342] p-2 hover:bg-slate-100 rounded-lg transition-all"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-slate-200 px-4 py-4 space-y-3" data-testid="mobile-nav-panel">
          <a 
            href="#services" 
            onClick={() => setMobileMenuOpen(false)}
            className="block text-slate-700 font-semibold py-2 px-3 rounded hover:bg-slate-50"
          >
            Services
          </a>
          <a 
            href="#calculator" 
            onClick={() => setMobileMenuOpen(false)}
            className="block text-slate-700 font-semibold py-2 px-3 rounded hover:bg-slate-50"
          >
            Price Estimator
          </a>
          <a 
            href="#how-it-works" 
            onClick={() => setMobileMenuOpen(false)}
            className="block text-slate-700 font-semibold py-2 px-3 rounded hover:bg-slate-50"
          >
            How it Works
          </a>
          <a 
            href="#contact" 
            onClick={() => setMobileMenuOpen(false)}
            className="block text-slate-700 font-semibold py-2 px-3 rounded hover:bg-slate-50"
          >
            Contact
          </a>
          {user ? (
            <div className="pt-3 border-t border-slate-100 space-y-3">
              <span className="block text-slate-500 px-3 font-medium">Signed in as {user.name}</span>
              {user.role === "admin" && (
                <Link 
                  to="/admin" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-[#0A2342] font-semibold py-2 px-3 rounded hover:bg-slate-50"
                >
                  Admin Portal
                </Link>
              )}
              <button 
                onClick={() => { logout(); setMobileMenuOpen(false); }}
                className="w-full text-left text-red-600 font-semibold py-2 px-3 rounded hover:bg-slate-50 flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" /> Sign Out
              </button>
            </div>
          ) : (
            <Link 
              to="/login" 
              onClick={() => setMobileMenuOpen(false)}
              className="block text-center bg-[#0A2342] text-white py-3 rounded-lg font-bold"
            >
              Sign In
            </Link>
          )}
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A2342] text-white py-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2 space-y-4">
            <div className="flex items-center space-x-3 text-white">
              <div className="p-2.5 bg-white/10 rounded-lg text-[#FF6B00]">
                <Wrench className="h-6 w-6 stroke-[2.5]" />
              </div>
              <span className="font-extrabold text-2xl tracking-tight uppercase">D&G MOBILE MECHANIC</span>
            </div>
            <p className="text-slate-300 max-w-sm text-sm">
              We bring dealer-quality tools and expert mechanics straight to your driveway, workplace, or roadside. Transparent pricing, immediate scheduling, and complete peace of mind.
            </p>
          </div>
          <div>
            <h4 className="font-extrabold text-sm uppercase tracking-widest text-[#FF6B00] mb-4">Contact Info</h4>
            <ul className="space-y-3 text-sm text-slate-300">
              <li className="flex items-center gap-2" data-testid="footer-phone">
                <Phone className="h-4 w-4 text-[#FF6B00]" />
                <a href="tel:+12059268519" className="hover:text-white transition-colors">(205) 926-8519</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-[#FF6B00]" />
                <a href="mailto:dispatch@dgmechanic.com" className="hover:text-white transition-colors">dispatch@dgmechanic.com</a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#FF6B00]" />
                <span>Serving Hampton, VA &amp; the Hampton Roads Area</span>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-extrabold text-sm uppercase tracking-widest text-[#FF6B00] mb-4">Hours of Operation</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>Monday - Friday: 2:00 PM - 9:00 PM</li>
              <li>Saturday: 8:00 AM - 6:00 PM</li>
              <li>Sunday: Emergency Dispatch Only</li>
              <li className="text-[#FF6B00] font-semibold mt-2">24/7 Roadside Assistance</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 mt-12 pt-8 text-center text-xs text-slate-400">
          <p>© {new Date().getFullYear()} D&G Mobile Mechanic Service. All rights reserved. Locally Owned & Operated.</p>
        </div>
      </div>
    </footer>
  );
}

// ----------------- Landing Page & Calculator -----------------
function LandingPage() {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);

  // Calculator states
  const [step, setStep] = useState(1);
  const [vehicle, setVehicle] = useState({ year: "2018", make: "", model: "", zip: "" });
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("Morning");

  // Booking result/success state
  const [bookingResult, setBookingResult] = useState(null);

  const addonsList = [
    { service_id: "engine-detailing", name: "Premium Engine Bay Detail", base_price: 45.00, description: "Degrease, steam clean, and dress exterior engine components." },
    { service_id: "windshield-wipers", name: "Heavy-Duty Windshield Wiper Replacement", base_price: 25.00, description: "OEM grade front wiper blades installed." },
    { service_id: "tire-rotation", name: "Driveway Tire Rotation & Brake Inspection", base_price: 35.00, description: "Precise rotation and assessment of wear/pad thickness." }
  ];

  useEffect(() => {
    async function loadPricing() {
      try {
        const res = await axios.get(`${API}/pricing`);
        setPricing(res.data);
      } catch (e) {
        toast.error("Failed to load live service rates.");
      } finally {
        setLoading(false);
      }
    }
    loadPricing();
  }, []);

  const totalCost = () => {
    let price = 0;
    selectedServices.forEach(svcId => {
      const item = pricing.find(p => p.service_id === svcId);
      if (item) price += item.base_price;
    });
    selectedAddons.forEach(addonId => {
      const item = addonsList.find(a => a.service_id === addonId);
      if (item) price += item.base_price;
    });
    return price;
  };

  const toggleService = (svcId) => {
    if (selectedServices.includes(svcId)) {
      setSelectedServices(selectedServices.filter(id => id !== svcId));
    } else {
      setSelectedServices([...selectedServices, svcId]);
    }
  };

  const toggleAddon = (addonId) => {
    if (selectedAddons.includes(addonId)) {
      setSelectedAddons(selectedAddons.filter(id => id !== addonId));
    } else {
      setSelectedAddons([...selectedAddons, addonId]);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.phone || !bookingDate) {
      toast.error("Please fill in all required fields.");
      return;
    }

    try {
      const payload = {
        client_name: contact.name,
        email: contact.email,
        phone: contact.phone,
        zip_code: vehicle.zip,
        vehicle_year: vehicle.year,
        vehicle_make: vehicle.make,
        vehicle_model: vehicle.model,
        services: selectedServices,
        addon_services: selectedAddons,
        booking_date: bookingDate,
        booking_time: bookingTime
      };

      const res = await axios.post(`${API}/bookings`, payload);
      setBookingResult(res.data);
      setStep(4);
      toast.success("Booking request submitted!", { description: "We have dispatched a mock SMS and email confirmation." });
    } catch (err) {
      const msg = err.response?.data?.detail || "Booking failed to submit.";
      toast.error("Error submitting request", { description: msg });
    }
  };

  const resetCalculator = () => {
    setStep(1);
    setVehicle({ year: "2018", make: "", model: "", zip: "" });
    setSelectedServices([]);
    setSelectedAddons([]);
    setContact({ name: "", email: "", phone: "" });
    setBookingDate("");
    setBookingTime("Morning");
    setBookingResult(null);
  };

  return (
    <div className="bg-[#F8FAFC] font-sans antialiased">
      {/* Hero Section */}
      <section className="relative bg-[#0A2342] text-white py-24 md:py-32 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1769677334898-402fdbb6bef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxtZWNoYW5pYyUyMHJlcGFpcmluZyUyMGNhciUyMGVuZ2luZSUyMG91dGRvb3JzfGVufDB8fHx8MTc4MjQxNTE3M3ww&ixlib=rb-4.1.0&q=85" 
            alt="D&G Mobile Mechanic" 
            className="w-full h-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A2342]/95 via-[#0A2342]/85 to-transparent"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6 text-left">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B00] bg-white/10 px-3 py-1.5 rounded-full inline-block">
              ⚡ Hampton &amp; Greater Hampton Roads, VA
            </span>
            <h1 className="text-5xl sm:text-6xl font-extrabold tracking-tighter text-white leading-tight" data-testid="hero-headline">
              Expert Mechanics. <br />
              <span className="text-[#FF6B00]">We Bring The Garage</span> <br />
              Direct To You.
            </h1>
            <p className="text-lg text-slate-300 max-w-xl">
              Skip the noisy waiting room and towing hassle. Our elite, certified mechanics service your vehicle directly in your driveway, workplace, or roadside. Instant upfront quotes and absolute trust.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <a 
                href="#calculator" 
                className="bg-[#FF6B00] text-white font-bold px-8 py-4 rounded-lg hover:-translate-y-1 hover:shadow-lg hover:shadow-[#FF6B00]/20 transition-all duration-200 text-center"
                data-testid="hero-cta-calc"
              >
                Estimate & Book Now
              </a>
              <a 
                href="tel:+12059268519" 
                className="border-2 border-white/30 hover:border-white text-white font-semibold px-8 py-4 rounded-lg hover:bg-white/5 transition-all text-center flex items-center justify-center gap-2"
                data-testid="hero-cta-call"
              >
                <Phone className="h-5 w-5" /> (205) 926-8519
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 hidden lg:block">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10 space-y-4">
              <div className="flex items-center space-x-2 text-[#FF6B00]">
                <Star className="h-5 w-5 fill-current" />
                <Star className="h-5 w-5 fill-current" />
                <Star className="h-5 w-5 fill-current" />
                <Star className="h-5 w-5 fill-current" />
                <Star className="h-5 w-5 fill-current" />
                <span className="text-white font-bold text-sm ml-2">4.9/5 Rating</span>
              </div>
              <p className="text-sm italic text-slate-200">
                &ldquo;D&amp;G changed my brake pads while I was working from home. No towing, no waiting rooms, and the quote was accurate to the penny. Absolute game changer!&rdquo;
              </p>
              <div className="border-t border-white/10 pt-4 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Sarah M., Hampton</span>
                <CheckCircle2 className="h-5 w-5 text-green-400" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services List Section */}
      <section id="services" className="py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B00]">Transparent Rates</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A2342]" data-testid="services-heading">
              Our Professional Services & Transparent Base Pricing
            </h2>
            <p className="text-[#475569]">
              Enjoy clear, straightforward rates with no unexpected markups. We service all major vehicle makes and models.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Wrench className="animate-spin h-8 w-8 text-[#0A2342]" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="services-grid">
              {pricing.map((svc) => (
                <div 
                  key={svc.service_id} 
                  className={`bg-white border rounded-xl p-6 relative flex flex-col justify-between transition-all hover:shadow-md ${svc.popular ? "border-[#FF6B00] ring-1 ring-[#FF6B00]" : "border-slate-200"}`}
                  data-testid={`service-card-${svc.service_id}`}
                >
                  {svc.popular && (
                    <span className="absolute -top-3 right-4 bg-[#FF6B00] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                      Most Popular
                    </span>
                  )}
                  <div className="space-y-3">
                    <span className="text-xs font-semibold text-[#FF6B00] uppercase tracking-wider">{svc.category}</span>
                    <h3 className="text-xl font-bold text-[#0A2342]">{svc.name}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{svc.description}</p>
                  </div>
                  <div className="border-t border-slate-100 mt-6 pt-4 flex items-center justify-between">
                    <div>
                      <span className="text-xs text-slate-400 block font-medium">Starting from</span>
                      <span className="text-2xl font-black text-[#0A2342]" data-testid={`service-price-${svc.service_id}`}>
                        ${svc.base_price.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center text-xs text-slate-500 font-semibold gap-1">
                      <Clock className="h-4 w-4" /> {svc.estimated_time}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pricing Estimator & Dynamic booking request form wizard */}
      <section id="calculator" className="py-24 bg-slate-50 border-t border-slate-200/50" data-testid="calculator-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B00]">Instant Quote Wizard</span>
            <h2 className="text-3xl font-bold tracking-tight text-[#0A2342]">Interactive Price Estimator</h2>
            <p className="text-slate-600 text-sm">
              Build your customized quote in real-time. Choose your services, choose add-ons, and book locally in under 3 minutes.
            </p>
          </div>

          <div className="bg-white border-2 border-[#0A2342]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-2xl overflow-hidden" data-testid="calculator-wizard-container">
            {/* Step indicators */}
            <div className="bg-[#0A2342] px-6 py-4 flex items-center justify-between border-b border-slate-700">
              <div className="flex items-center space-x-3 text-white">
                <span className="bg-[#FF6B00] text-white font-extrabold h-8 w-8 rounded-full flex items-center justify-center text-sm">
                  {step}
                </span>
                <div>
                  <h4 className="text-sm font-bold uppercase tracking-wider">
                    {step === 1 && "Step 1: Vehicle & Dispatch Info"}
                    {step === 2 && "Step 2: Select Services & Upgrades"}
                    {step === 3 && "Step 3: Schedule & Contact Details"}
                    {step === 4 && "Booking Submitted Successfully!"}
                  </h4>
                  <p className="text-xs text-slate-300">
                    {step === 1 && "Provide basic details so we can quote correctly"}
                    {step === 2 && "Pick starting packages and optional upgrades"}
                    {step === 3 && "Confirm the dispatch time and client contact"}
                    {step === 4 && "See details of your mobile service dispatch"}
                  </p>
                </div>
              </div>
              <span className="text-xs text-slate-300 font-bold hidden sm:inline uppercase">
                {step < 4 ? `Step ${step} of 3` : "Complete"}
              </span>
            </div>

            <div className="p-8">
              {step === 1 && (
                <div className="space-y-6" data-testid="wizard-step-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-semibold text-[#0F172A] mb-2 block">Vehicle Year *</label>
                      <select 
                        value={vehicle.year}
                        onChange={(e) => setVehicle({...vehicle, year: e.target.value})}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-3 px-4 bg-white border"
                        data-testid="calc-vehicle-year"
                      >
                        {Array.from({length: 26}, (_, i) => String(2026 - i)).map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#0F172A] mb-2 block">Zip Code (Hampton Roads Service Area) *</label>
                      <input 
                        type="text" 
                        placeholder="e.g., 23666"
                        value={vehicle.zip}
                        onChange={(e) => setVehicle({...vehicle, zip: e.target.value})}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-3 px-4 border"
                        data-testid="calc-vehicle-zip"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-semibold text-[#0F172A] mb-2 block">Vehicle Make *</label>
                      <input 
                        type="text" 
                        placeholder="e.g., Toyota, Ford, Honda"
                        value={vehicle.make}
                        onChange={(e) => setVehicle({...vehicle, make: e.target.value})}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-3 px-4 border"
                        data-testid="calc-vehicle-make"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#0F172A] mb-2 block">Vehicle Model *</label>
                      <input 
                        type="text" 
                        placeholder="e.g., Camry, F-150, Civic"
                        value={vehicle.model}
                        onChange={(e) => setVehicle({...vehicle, model: e.target.value})}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-3 px-4 border"
                        data-testid="calc-vehicle-model"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-slate-100">
                    <button 
                      onClick={() => {
                        if (!vehicle.make || !vehicle.model || !vehicle.zip) {
                          toast.error("Please fill in vehicle details and zip code.");
                          return;
                        }
                        setStep(2);
                      }}
                      className="bg-[#0A2342] text-white font-semibold px-8 py-3.5 rounded-lg hover:bg-[#113059] transition-all flex items-center gap-2"
                      data-testid="wizard-next-step-1"
                    >
                      Choose Services <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-8" data-testid="wizard-step-2">
                  <div>
                    <h5 className="font-bold text-slate-800 mb-3 text-sm uppercase tracking-wider">Select Primary Service Packages</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {pricing.map(svc => (
                        <div 
                          key={svc.service_id}
                          onClick={() => toggleService(svc.service_id)}
                          className={`border-2 rounded-xl p-4 cursor-pointer transition-all flex items-start gap-3 select-none ${selectedServices.includes(svc.service_id) ? "border-[#FF6B00] bg-[#FF6B00]/5 ring-1 ring-[#FF6B00]" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                          data-testid={`calc-svc-item-${svc.service_id}`}
                        >
                          <div className={`h-5 w-5 rounded border mt-1 flex items-center justify-center transition-all ${selectedServices.includes(svc.service_id) ? "border-[#FF6B00] bg-[#FF6B00]" : "border-slate-300"}`}>
                            {selectedServices.includes(svc.service_id) && <CheckCircle2 className="h-4 w-4 text-white" />}
                          </div>
                          <div className="flex-1">
                            <span className="text-xs font-bold text-slate-400 block uppercase">{svc.category}</span>
                            <span className="font-bold text-[#0A2342] block text-sm">{svc.name}</span>
                            <span className="text-xs text-slate-500 block mt-1">{svc.estimated_time}</span>
                            <span className="text-sm font-black text-[#FF6B00] block mt-2">${svc.base_price.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h5 className="font-bold text-slate-800 mb-3 text-sm uppercase tracking-wider">Optional On-Site Add-Ons</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {addonsList.map(addon => (
                        <div 
                          key={addon.service_id}
                          onClick={() => toggleAddon(addon.service_id)}
                          className={`border rounded-xl p-4 cursor-pointer transition-all flex items-start gap-3 select-none ${selectedAddons.includes(addon.service_id) ? "border-slate-800 bg-slate-50" : "border-slate-200 bg-white"}`}
                          data-testid={`calc-addon-item-${addon.service_id}`}
                        >
                          <div className={`h-5 w-5 rounded border mt-1 flex items-center justify-center transition-all ${selectedAddons.includes(addon.service_id) ? "border-slate-800 bg-slate-800" : "border-slate-300"}`}>
                            {selectedAddons.includes(addon.service_id) && <CheckCircle2 className="h-4 w-4 text-white" />}
                          </div>
                          <div className="flex-1">
                            <span className="font-bold text-[#0A2342] block text-sm">{addon.name}</span>
                            <span className="text-xs text-slate-500 block mt-1">{addon.description}</span>
                            <span className="text-xs font-bold text-slate-700 block mt-1">+ ${addon.base_price.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-6 border-t border-slate-100 bg-slate-50 -mx-8 -mb-8 p-8">
                    <div>
                      <span className="text-xs text-slate-400 font-bold block uppercase tracking-wider">Real-time estimate</span>
                      <span className="text-3xl font-black text-[#0A2342]" data-testid="calc-live-total">
                        ${totalCost().toFixed(2)}
                      </span>
                    </div>
                    <div className="flex gap-3 justify-end">
                      <button 
                        onClick={() => setStep(1)}
                        className="border border-slate-300 text-slate-600 font-semibold px-6 py-3 rounded-lg hover:bg-slate-100 transition-all text-sm"
                        data-testid="wizard-prev-step-2"
                      >
                        Back
                      </button>
                      <button 
                        onClick={() => {
                          if (selectedServices.length === 0) {
                            toast.error("Please select at least one core service.");
                            return;
                          }
                          setStep(3);
                        }}
                        className="bg-[#0A2342] text-white font-semibold px-8 py-3.5 rounded-lg hover:bg-[#113059] transition-all flex items-center gap-2 text-sm"
                        data-testid="wizard-next-step-2"
                      >
                        Schedule Dispatch <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <form onSubmit={handleBookingSubmit} className="space-y-6" data-testid="wizard-step-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-semibold text-[#0F172A] mb-2 block">Preferred Appointment Date *</label>
                      <input 
                        type="date" 
                        value={bookingDate}
                        onChange={(e) => setBookingDate(e.target.value)}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-3 px-4 border"
                        data-testid="calc-booking-date"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#0F172A] mb-2 block">Preferred Arrival Window *</label>
                      <select 
                        value={bookingTime}
                        onChange={(e) => setBookingTime(e.target.value)}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-3 px-4 bg-white border"
                        data-testid="calc-booking-time"
                        required
                      >
                        <option value="Morning">Morning (8:00 AM - 12:00 PM)</option>
                        <option value="Afternoon">Afternoon (12:00 PM - 4:00 PM)</option>
                        <option value="Evening">Evening (4:00 PM - 8:00 PM)</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="sm:col-span-1">
                      <label className="text-sm font-semibold text-[#0F172A] mb-2 block">Full Name *</label>
                      <input 
                        type="text" 
                        placeholder="John Doe"
                        value={contact.name}
                        onChange={(e) => setContact({...contact, name: e.target.value})}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-3 px-4 border"
                        data-testid="calc-contact-name"
                        required
                      />
                    </div>
                    <div className="sm:col-span-1">
                      <label className="text-sm font-semibold text-[#0F172A] mb-2 block">Phone Number *</label>
                      <input 
                        type="tel" 
                        placeholder="(555) 555-5555"
                        value={contact.phone}
                        onChange={(e) => setContact({...contact, phone: e.target.value})}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-3 px-4 border"
                        data-testid="calc-contact-phone"
                        required
                      />
                    </div>
                    <div className="sm:col-span-1">
                      <label className="text-sm font-semibold text-[#0F172A] mb-2 block">Email Address (Optional)</label>
                      <input 
                        type="email" 
                        placeholder="john@example.com"
                        value={contact.email}
                        onChange={(e) => setContact({...contact, email: e.target.value})}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-3 px-4 border"
                        data-testid="calc-contact-email"
                      />
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
                    <div className="flex items-center text-slate-700 text-sm gap-2">
                      <Phone className="h-4 w-4 text-[#FF6B00]" />
                      <strong>Static Booking Note:</strong>
                    </div>
                    <p className="text-slate-600 text-xs leading-relaxed">
                      This system handles mock booking and dispatch requests. Once submitted, our team receives your vehicle data locally. You can also expedite or modify your schedule instantly by dialing <a href="tel:+12059268519" className="text-[#FF6B00] font-bold underline">(205) 926-8519</a>.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-6 border-t border-slate-100 bg-slate-50 -mx-8 -mb-8 p-8">
                    <div>
                      <span className="text-xs text-slate-400 font-bold block uppercase tracking-wider">Estimated Invoice Total</span>
                      <span className="text-3xl font-black text-[#0A2342]" data-testid="calc-final-total">
                        ${totalCost().toFixed(2)}
                      </span>
                    </div>
                    <div className="flex gap-3 justify-end">
                      <button 
                        type="button"
                        onClick={() => setStep(2)}
                        className="border border-slate-300 text-slate-600 font-semibold px-6 py-3 rounded-lg hover:bg-slate-100 transition-all text-sm"
                        data-testid="wizard-prev-step-3"
                      >
                        Back
                      </button>
                      <button 
                        type="submit"
                        className="bg-[#FF6B00] hover:bg-[#E65A00] text-white font-bold px-8 py-3.5 rounded-lg transition-all flex items-center gap-2 text-sm shadow-md"
                        data-testid="calc-submit-booking-btn"
                      >
                        Request Mobile Dispatch <CheckCircle2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {step === 4 && bookingResult && (
                <div className="text-center space-y-6 py-4" data-testid="wizard-step-success">
                  <div className="inline-flex p-3 bg-green-100 text-green-600 rounded-full mb-2">
                    <CheckCircle2 className="h-10 w-10 stroke-[2.5]" />
                  </div>
                  <h3 className="text-2xl font-black text-[#0A2342]">Mobile Mechanic Scheduled!</h3>
                  <p className="text-slate-600 max-w-lg mx-auto text-sm">
                    Thank you, {bookingResult.client_name}. We have saved your booking request for the <strong className="text-[#0A2342]">{bookingResult.booking_year || vehicle.year} {bookingResult.vehicle_make || vehicle.make}</strong> in our local database.
                  </p>

                  <div className="max-w-xl mx-auto border border-slate-200 rounded-xl p-5 bg-slate-50 text-left space-y-3">
                    <h5 className="font-extrabold text-[#0A2342] text-sm uppercase tracking-wider border-b border-slate-200 pb-2">
                      Local Notification Logs (Simulated SMS & Email)
                    </h5>
                    
                    <div className="space-y-3">
                      <div>
                        <span className="text-xs font-bold text-slate-500 block">📱 SIMULATED SMS SENT TO {bookingResult.phone}:</span>
                        <div className="p-3 bg-slate-950 text-emerald-400 font-mono text-xs rounded-lg mt-1 whitespace-pre-wrap border" data-testid="mock-sms-log">
                          {bookingResult.sms_logs?.[0]}
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-xs font-bold text-slate-500 block">📧 SIMULATED EMAIL LOG:</span>
                        <div className="p-3 bg-white text-slate-700 font-mono text-xs rounded-lg mt-1 whitespace-pre-wrap border border-slate-200 max-h-32 overflow-y-auto" data-testid="mock-email-log">
                          {bookingResult.email_logs?.[0]}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-slate-100 flex flex-col sm:flex-row justify-center gap-4">
                    <button 
                      onClick={resetCalculator}
                      className="bg-[#0A2342] text-white font-bold px-6 py-3 rounded-lg hover:bg-[#113059] transition-all text-sm"
                      data-testid="calculator-reset-btn"
                    >
                      Calculate New Vehicle
                    </button>
                    <a 
                      href="tel:+12059268519"
                      className="border border-slate-300 text-slate-700 font-semibold px-6 py-3 rounded-lg hover:bg-slate-100 transition-all text-sm flex items-center justify-center gap-1"
                    >
                      <Phone className="h-4 w-4" /> Call to Modify
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works (Roadmap timeline) */}
      <section id="how-it-works" className="py-24 bg-white border-t border-slate-200/50" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B00]">Service Flow</span>
            <h2 className="text-3xl font-bold tracking-tight text-[#0A2342]">How D&G Mobile Works</h2>
            <p className="text-[#475569]">
              Three simple steps to professional automotive maintenance without leaving your living room.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
            <div className="space-y-4 text-left p-6 border border-slate-100 rounded-xl relative bg-white shadow-sm hover:shadow-md transition-shadow">
              <span className="text-5xl font-black text-slate-200 block">01</span>
              <h4 className="text-xl font-bold text-[#0A2342]">Get Instant Price Quote</h4>
              <p className="text-slate-600 text-sm leading-relaxed">
                Use our dynamic price wizard above. Input your vehicle year, make, and target zip, then choose your synthetic oil change or brake upgrade.
              </p>
            </div>

            <div className="space-y-4 text-left p-6 border border-slate-100 rounded-xl relative bg-white shadow-sm hover:shadow-md transition-shadow">
              <span className="text-5xl font-black text-slate-200 block">02</span>
              <h4 className="text-xl font-bold text-[#0A2342]">We Arrive On Location</h4>
              <p className="text-slate-600 text-sm leading-relaxed">
                Our certified mobile mechanics bring tools, parts, and fluid recovery direct to your home or office. Absolutely zero wait times.
              </p>
            </div>

            <div className="space-y-4 text-left p-6 border border-slate-100 rounded-xl relative bg-white shadow-sm hover:shadow-md transition-shadow">
              <span className="text-5xl font-black text-slate-200 block">03</span>
              <h4 className="text-xl font-bold text-[#0A2342]">Secure Digital Sign-Off</h4>
              <p className="text-slate-600 text-sm leading-relaxed">
                Review the completed diagnostics and repair live with your mechanic. Pay instantly upon completion with a complete digital ledger.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Static contact and service areas section */}
      <section id="contact" className="py-24 bg-slate-900 text-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6 space-y-6 text-left">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B00]">Local Dispatch</span>
            <h2 className="text-4xl font-black tracking-tight" data-testid="contact-headline">
              Ready to Book? <br />
              <span className="text-[#FF6B00]">Talk to Our Dispatcher</span>
            </h2>
            <p className="text-slate-300">
              Need immediate emergency roadside rescue or customized fleet pricing? Contact our dispatchers directly. We&apos;re locally owned and staffed by certified professionals.
            </p>

            <div className="space-y-4 border-t border-white/10 pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/10 text-[#FF6B00] rounded-lg">
                  <Phone className="h-6 w-6" />
                </div>
                <div>
                  <span className="text-xs text-slate-400 block font-bold uppercase tracking-wider">Dispatch Hotline</span>
                  <a href="tel:+12059268519" className="text-2xl font-black hover:text-[#FF6B00] transition-colors" data-testid="contact-phone-link">
                    (205) 926-8519
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/10 text-[#FF6B00] rounded-lg">
                  <MapPin className="h-6 w-6" />
                </div>
                <div>
                  <span className="text-xs text-slate-400 block font-bold uppercase tracking-wider">Mobile Hub Location</span>
                  <span className="text-base text-slate-200">Hampton, Newport News, Norfolk, Virginia Beach &amp; Chesapeake, VA</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 bg-white/5 border border-white/10 rounded-2xl p-8 space-y-6 text-left">
            <h4 className="text-xl font-bold flex items-center gap-2">
              <MapIcon className="text-[#FF6B00] h-5 w-5" /> Live Service Area Radius
            </h4>
            <p className="text-slate-300 text-sm">
              We dispatch within a 35-mile radius from Hampton, VA. If you reside outside of these boundaries, call us for emergency custom routing.
            </p>
            <div className="grid grid-cols-2 gap-4 text-xs font-bold text-slate-300 uppercase tracking-widest bg-white/5 p-4 rounded-lg">
              <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Hampton</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Newport News</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Norfolk</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Virginia Beach</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Chesapeake</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Yorktown</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ----------------- LoginPage -----------------
function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/");
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (!res.success) {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-slate-50 py-12 px-4 sm:px-6 lg:px-8" data-testid="login-page">
      <div className="max-w-md w-full space-y-8 bg-white p-8 border border-slate-200 rounded-2xl shadow-sm">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-[#0A2342] text-[#FF6B00] rounded-xl flex items-center justify-center mb-4">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h2 className="text-3xl font-extrabold text-[#0A2342]">Sign In</h2>
          <p className="mt-2 text-sm text-slate-500">
            Access secure D&G Mechanic Administration Portal
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3.5 rounded-lg flex items-center gap-2" data-testid="login-error-msg">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-[#0F172A] mb-2 block">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@dgmechanic.com"
                className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-3 px-4 border"
                data-testid="login-email-input"
                required
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#0F172A] mb-2 block">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="dgadmin987!"
                className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-3 px-4 border"
                data-testid="login-password-input"
                required
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#0A2342] hover:bg-[#113059] text-white font-bold py-3.5 px-4 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-[#FF6B00] disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {submitting ? "Signing In..." : "Sign In to Admin Portal"}
            </button>
          </div>
        </form>

        <div className="text-center pt-2">
          <Link to="/" className="text-sm text-slate-500 hover:text-[#0A2342] font-semibold transition-colors">
            ← Return to Public Website
          </Link>
        </div>
      </div>
    </div>
  );
}

// ----------------- AdminDashboard -----------------
function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("bookings"); // bookings, pricing
  const [bookings, setBookings] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);

  // New Pricing Item state
  const [newItem, setNewItem] = useState({ service_id: "", name: "", category: "Maintenance", base_price: "", description: "", estimated_time: "" });

  const loadData = async () => {
    setLoading(true);
    try {
      const [bRes, pRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/pricing`)
      ]);
      setBookings(bRes.data);
      setPricing(pRes.data);
    } catch (e) {
      toast.error("Failed to load dashboard statistics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleUpdateStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}`, { status });
      toast.success(`Booking status updated to ${status}`);
      loadData();
    } catch (e) {
      toast.error("Failed to update status.");
    }
  };

  const handleCreatePricing = async (e) => {
    e.preventDefault();
    if (!newItem.service_id || !newItem.name || !newItem.base_price) {
      toast.error("Please fill in required pricing details.");
      return;
    }
    try {
      await axios.post(`${API}/pricing`, newItem);
      toast.success("New pricing service added!");
      setNewItem({ service_id: "", name: "", category: "Maintenance", base_price: "", description: "", estimated_time: "" });
      loadData();
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to add service item.";
      toast.error(msg);
    }
  };

  const handleDeletePricing = async (svcId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    try {
      await axios.delete(`${API}/pricing/${svcId}`);
      toast.success("Service deleted.");
      loadData();
    } catch (e) {
      toast.error("Failed to delete service.");
    }
  };

  return (
    <div className="bg-slate-50 min-h-[90vh] py-10" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-5">
          <div className="text-left">
            <h1 className="text-3xl font-black text-[#0A2342] tracking-tight">D&G Administration Hub</h1>
            <p className="text-sm text-slate-500">Manage dispatch bookings, modify pricing structures, and view logs.</p>
          </div>
          <div className="flex bg-white p-1 rounded-lg border border-slate-200 self-start">
            <button 
              onClick={() => setActiveTab("bookings")}
              className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${activeTab === "bookings" ? "bg-[#0A2342] text-white" : "text-slate-600 hover:text-[#0A2342]"}`}
              data-testid="tab-bookings-btn"
            >
              Bookings ({bookings.length})
            </button>
            <button 
              onClick={() => setActiveTab("pricing")}
              className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${activeTab === "pricing" ? "bg-[#0A2342] text-white" : "text-slate-600 hover:text-[#0A2342]"}`}
              data-testid="tab-pricing-btn"
            >
              Services / Pricing
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20 bg-white border border-slate-200 rounded-xl shadow-sm">
            <div className="text-center">
              <Wrench className="animate-spin h-8 w-8 text-[#0A2342] mx-auto mb-3" />
              <p className="text-slate-500 text-sm font-medium">Refreshing database data...</p>
            </div>
          </div>
        ) : (
          <div>
            {activeTab === "bookings" && (
              <div className="space-y-6" data-testid="admin-bookings-tab">
                <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
                  <div className="px-6 py-4 bg-[#0A2342] text-white">
                    <h3 className="font-bold">Active Booking & Service Dispatches</h3>
                  </div>
                  
                  {bookings.length === 0 ? (
                    <div className="p-12 text-center text-slate-500">
                      No customer booking requests found in the database.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse" data-testid="admin-bookings-table">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200 text-slate-700 text-xs font-bold uppercase tracking-wider">
                            <th className="p-4">Customer Info</th>
                            <th className="p-4">Vehicle</th>
                            <th className="p-4">Schedule</th>
                            <th className="p-4">Details & Cost</th>
                            <th className="p-4">Status</th>
                            <th className="p-4 text-right">Dispatch Control</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-sm">
                          {bookings.map(b => (
                            <tr key={b.id || b._id} className="hover:bg-slate-50/50" data-testid={`booking-row-${b.id || b._id}`}>
                              <td className="p-4">
                                <div className="font-bold text-[#0A2342]">{b.client_name}</div>
                                <div className="text-xs text-slate-500 mt-0.5">{b.phone}</div>
                                {b.email && <div className="text-xs text-slate-400">{b.email}</div>}
                              </td>
                              <td className="p-4 font-semibold text-slate-700">
                                {b.vehicle_year} {b.vehicle_make} {b.vehicle_model}
                                <span className="block text-xs text-slate-400 font-medium">Zip: {b.zip_code}</span>
                              </td>
                              <td className="p-4">
                                <div className="font-bold text-slate-800">{b.booking_date}</div>
                                <div className="text-xs text-slate-500 font-medium">{b.booking_time}</div>
                              </td>
                              <td className="p-4">
                                <div className="text-xs space-y-1">
                                  <div className="text-slate-600 font-medium">
                                    Services: {b.services?.join(", ")}
                                  </div>
                                  {b.addon_services?.length > 0 && (
                                    <div className="text-[#FF6B00] font-medium">
                                      Addons: {b.addon_services.join(", ")}
                                    </div>
                                  )}
                                </div>
                                <div className="font-black text-[#0A2342] mt-1 text-base">
                                  ${b.estimated_total?.toFixed(2)}
                                </div>
                              </td>
                              <td className="p-4">
                                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                                  b.status === "Confirmed" ? "bg-emerald-100 text-emerald-800" :
                                  b.status === "Cancelled" ? "bg-rose-100 text-rose-800" :
                                  b.status === "Completed" ? "bg-blue-100 text-blue-800" :
                                  "bg-amber-100 text-amber-800"
                                }`} data-testid={`booking-status-${b.id || b._id}`}>
                                  {b.status}
                                </span>
                              </td>
                              <td className="p-4 text-right">
                                <select 
                                  value={b.status}
                                  onChange={(e) => handleUpdateStatus(b.id || b._id, e.target.value)}
                                  className="border border-slate-300 rounded p-1.5 text-xs bg-white font-semibold text-slate-700 focus:ring-2 focus:ring-[#0A2342]/20"
                                  data-testid={`booking-status-select-${b.id || b._id}`}
                                >
                                  <option value="Pending">Pending</option>
                                  <option value="Confirmed">Confirm</option>
                                  <option value="Completed">Completed</option>
                                  <option value="Cancelled">Cancelled</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "pricing" && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="admin-pricing-tab">
                {/* Form to add pricing */}
                <div className="lg:col-span-4 bg-white border border-slate-200 rounded-xl p-6 shadow-sm self-start">
                  <h3 className="font-extrabold text-[#0A2342] mb-4 text-lg">Add Service Offering</h3>
                  <form onSubmit={handleCreatePricing} className="space-y-4">
                    <div>
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 block">Service Key (unique-id) *</label>
                      <input 
                        type="text" 
                        placeholder="e.g., spark-plugs"
                        value={newItem.service_id}
                        onChange={(e) => setNewItem({...newItem, service_id: e.target.value})}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-2 px-3 border text-sm"
                        data-testid="add-svc-id-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 block">Service Name *</label>
                      <input 
                        type="text" 
                        placeholder="e.g., Brake Rotor Service"
                        value={newItem.name}
                        onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-2 px-3 border text-sm"
                        data-testid="add-svc-name-input"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 block">Base Price ($) *</label>
                        <input 
                          type="number" 
                          step="0.01"
                          placeholder="e.g., 149"
                          value={newItem.base_price}
                          onChange={(e) => setNewItem({...newItem, base_price: e.target.value})}
                          className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-2 px-3 border text-sm"
                          data-testid="add-svc-price-input"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 block">Estimated Time</label>
                        <input 
                          type="text" 
                          placeholder="e.g., 1.5 hrs"
                          value={newItem.estimated_time}
                          onChange={(e) => setNewItem({...newItem, estimated_time: e.target.value})}
                          className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-2 px-3 border text-sm"
                          data-testid="add-svc-time-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 block">Category</label>
                      <select 
                        value={newItem.category}
                        onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-2.5 px-3 border bg-white text-sm"
                        data-testid="add-svc-category-select"
                      >
                        <option value="Maintenance">Maintenance</option>
                        <option value="Brakes">Brakes</option>
                        <option value="Diagnostics">Diagnostics</option>
                        <option value="Electrical">Electrical</option>
                        <option value="Engine">Engine</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 block">Description</label>
                      <textarea 
                        placeholder="Provide details about what includes..."
                        value={newItem.description}
                        onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                        className="w-full border-slate-300 focus:border-[#0A2342] focus:ring-2 focus:ring-[#0A2342]/20 rounded-md py-2 px-3 border text-sm"
                        rows="3"
                        data-testid="add-svc-desc-input"
                      />
                    </div>
                    <button 
                      type="submit"
                      className="w-full bg-[#FF6B00] hover:bg-[#E65A00] text-white font-bold py-2.5 px-4 rounded-lg transition-all text-sm shadow-sm"
                      data-testid="add-svc-submit-btn"
                    >
                      Add Offering
                    </button>
                  </form>
                </div>

                {/* Catalog Table */}
                <div className="lg:col-span-8 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="px-6 py-4 bg-[#0A2342] text-white">
                    <h3 className="font-bold">Catalog Pricing Ledger</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse" data-testid="admin-pricing-table">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-slate-700 text-xs font-bold uppercase tracking-wider">
                          <th className="p-4">Key</th>
                          <th className="p-4">Name</th>
                          <th className="p-4">Category</th>
                          <th className="p-4">Base Price</th>
                          <th className="p-4">Time</th>
                          <th className="p-4 text-right">Delete</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-sm">
                        {pricing.map(item => (
                          <tr key={item.service_id} className="hover:bg-slate-50/50" data-testid={`pricing-row-${item.service_id}`}>
                            <td className="p-4 font-mono font-bold text-xs text-slate-500">{item.service_id}</td>
                            <td className="p-4 font-semibold text-[#0A2342]">{item.name}</td>
                            <td className="p-4">
                              <span className="bg-slate-100 text-slate-700 text-xs font-bold px-2 py-0.5 rounded">
                                {item.category}
                              </span>
                            </td>
                            <td className="p-4 font-black text-[#0A2342]">${item.base_price.toFixed(2)}</td>
                            <td className="p-4 text-xs font-semibold text-slate-500">{item.estimated_time}</td>
                            <td className="p-4 text-right">
                              <button 
                                onClick={() => handleDeletePricing(item.service_id)}
                                className="text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 rounded"
                                data-testid={`pricing-delete-btn-${item.service_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------- Main App Component -----------------
function App() {
  return (
    <div className="App flex flex-col min-h-screen bg-[#F8FAFC]">
      <AuthProvider>
        <Router>
          <Header />
          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute role="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
        </Router>
      </AuthProvider>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default App;
